import { DomainFormSchema } from "@/schemas/domain-form.schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "./ui/form";
import { Input } from "./ui/input";
import { Button } from "./ui/button";


interface Props {
    companyId: string
    onSubmit: (data: z.infer<typeof DomainFormSchema>) => void,
    isLoading: boolean
    className?: string
}

export default function DomainForm(props: Props) {
    const form = useForm<z.infer<typeof DomainFormSchema>>({
        resolver: zodResolver(DomainFormSchema),
        defaultValues: {
            Name: "",
            Website: "https://clearbook.dk",
            Folder: "",
            Admin: "admin",
            CompanyID: props.companyId,
            OwnerEmail: "",
            Version: "1.0.0",
            Key: "",
            DBRel: "",
            DBServer: "",
            DBName: "",
            DBUser: "",
            DBPassword: "",
            Package: "",
            PaymentPackage: "",
        },
    })

    return (
        <Form {...form}>
            <form className={`grid gap-4 ${props.className}`} onSubmit={form.handleSubmit(props.onSubmit)}>


                <div className="grid grid-cols-2 gap-4">

                    {/* First column */}

                    <div className="flex flex-col gap-4">
                        <h2 className="text-2xl font-semibold">Domain</h2>
                        <FormField
                            control={form.control}
                            name="Name"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Name</FormLabel>
                                    <FormControl>
                                        <Input placeholder="Name" {...field} required />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="Website"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Website</FormLabel>
                                    <FormControl>
                                        <Input placeholder="Website" {...field} readOnly />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="Folder"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Booking Folder</FormLabel>
                                    <FormDescription> https://clearbook.dk/booking/[Booking Folder] </FormDescription>
                                    <FormControl>
                                        <Input placeholder="Folder" {...field} required />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="Admin"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Admin Folder</FormLabel>
                                    <FormDescription> https://clearbook.dk/booking/[Booking Folder]/admin </FormDescription>
                                    <FormControl>
                                        <Input placeholder="Admin Folder" {...field} readOnly />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="OwnerEmail"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Owner Email</FormLabel>
                                    <FormControl>
                                        <Input placeholder="Owner Email" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <h2 className="text-2xl font-semibold">Version</h2>
                        <FormField
                            control={form.control}
                            name="Version"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>ClearBook Version</FormLabel>
                                    <FormControl>
                                        <Input placeholder="ClearBook Version" {...field} readOnly />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                    {/* End of first column */}


                    {/* Second column */}

                    <div className="flex flex-col gap-4">
                        <h2 className="text-2xl font-semibold">Database</h2>
                        <FormField
                            control={form.control}
                            name="Key"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Database Key</FormLabel>
                                    <FormControl>
                                        <Input placeholder="Database Key" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="DBRel"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Database Rel</FormLabel>
                                    <FormControl>
                                        <Input placeholder="Database Release" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="DBServer"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Database Server</FormLabel>
                                    <FormControl>
                                        <Input placeholder="Database Server" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="DBName"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Database Name</FormLabel>
                                    <FormControl>
                                        <Input placeholder="Database Name" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="DBUser"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Database User</FormLabel>
                                    <FormControl>
                                        <Input placeholder="Database User" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="DBPassword"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Database Password</FormLabel>
                                    <FormControl>
                                        <Input type="password" placeholder="Database Password" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <h2 className="text-2xl font-semibold">Package</h2>
                        <FormField
                            control={form.control}
                            name="Package"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Package</FormLabel>
                                    <FormControl>
                                        <Input placeholder="Package" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="PaymentPackage"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Payment Package</FormLabel>
                                    <FormControl>
                                        <Input placeholder="Payment Package" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                    {/* End of second column */}

                </div>


                <Button type="submit" disabled={props.isLoading}>
                    Create Domain
                </Button>
            </form>
        </Form>
    )
}